<template>
  <section class="faq-section">
    <!-- Section Header -->
    <div class="faq-header">
      <h3 class="faq-title">
        <i>F</i>AQ
      </h3>
    </div>

    <!-- FAQ Items -->
    <div class="faq-list">
      <div
        v-for="(item, index) in faqItems"
        :key="index"
        class="faq-item"
      >
        <!-- Question -->
        <button
          @click="toggleItem(index)"
          class="faq-question-button"
        >
          <span class="faq-question-text">
            {{ item.question }}
          </span>

          <!-- Toggle Icon -->
          <div class="faq-toggle-icon">
            <svg
              v-if="!openItems.includes(index)"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M8 3.5V12.5M12.5 8H3.5" stroke="#3A55A6" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <svg
              v-else
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M12.5 8H3.5" stroke="#3A55A6" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </div>
        </button>

        <!-- Answer -->
        <transition
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="max-h-0 opacity-0"
          enter-to-class="max-h-96 opacity-100"
          leave-active-class="transition-all duration-300 ease-in"
          leave-from-class="max-h-96 opacity-100"
          leave-to-class="max-h-0 opacity-0"
        >
          <div v-if="openItems.includes(index)" class="faq-answer-wrapper">
            <div class="faq-answer-text">
              {{ item.answer }}
            </div>
          </div>
        </transition>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'

const openItems = ref<number[]>([0]) // First item open by default

const toggleItem = (index: number) => {
  const itemIndex = openItems.value.indexOf(index)
  if (itemIndex > -1) {
    openItems.value.splice(itemIndex, 1)
  } else {
    openItems.value.push(index)
  }
}

const faqItems = [
  {
    question: 'What currencies can I gift?',
    answer: 'You can fund your crypto gift card with USDC'
  },
  {
    question: 'How do I give a crypto gift card to a friend?',
    answer: 'Simply create a gift card, customize it with your message, top it up with USDC and share the generated link and PIN code with your friend via any messaging platform.'
  },
  {
    question: 'Can I change the selected currency after creating the gift card?',
    answer: 'No, once a gift card is created and funded, the currency cannot be changed. Please make sure to select the correct currency before funding.'
  },
  {
    question: 'Do I need the recipient\'s wallet address?',
    answer: 'No! That\'s the beauty of crypto gift cards. Your recipient will provide their own wallet address when they claim the gift.'
  },
  {
    question: 'Is there a time limit for claiming the gift?',
    answer: 'No, there is no expiration date. The recipient can claim their gift whenever they\'re ready.'
  },
  {
    question: 'Is Changelly safe to use?',
    answer: 'Yes, Changelly is a trusted crypto exchange platform that has been operating since 2015. It uses industry-standard security measures to protect your transactions.'
  },
  {
    question: 'Can I see whether the gift has been claimed?',
    answer: 'Yes, you\'ll receive a notification once your gift has been successfully claimed by the recipient.'
  },
  {
    question: 'What if the recipient doesn\'t have a crypto wallet?',
    answer: 'They can easily create one! We provide guidance on setting up a wallet during the claim process. It only takes a few minutes.'
  },
  {
    question: 'Is there a minimum or maximum gift amount?',
    answer: 'Minimum and maximum amounts may vary depending on the selected cryptocurrency and current network conditions. These limits will be shown when you create your gift.'
  },
  {
    question: 'Are there fees for crypto gift cards?',
    answer: 'There may be small network fees for processing blockchain transactions. Any applicable fees will be clearly shown before you confirm your purchase.'
  }
]
</script>

<style scoped>
.faq-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 20px 16px;
  gap: 24px;
  background: #FFFFFF;
  border-radius: 24px;
  max-width: 100%;
  overflow: hidden;
}

.faq-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  align-self: stretch;
}

.faq-title {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 29px;
  letter-spacing: 0.01em;
  color: #182855;
}

.faq-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 4px;
  align-self: stretch;
}

.faq-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px 12px;
  gap: 12px;
  background: #EAEFFF;
  border-radius: 12px;
  align-self: stretch;
}

.faq-question-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 12px;
  align-self: stretch;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  width: 100%;
}

.faq-question-text {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  line-height: 18px;
  letter-spacing: 0.01em;
  color: #182855;
  flex-grow: 1;
}

.faq-toggle-icon {
  width: 24px;
  height: 24px;
  background: #FFFFFF;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.faq-answer-wrapper {
  overflow: hidden;
}

.faq-answer-text {
  font-family: 'Inter Tight', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 15px;
  letter-spacing: 0.01em;
  color: #2D4280;
}
</style>
